import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

interface TabLevelFilter {
  [key: string]: string | null;
}

interface GlobalSearchAndFiltersState {
  searchQuery: string;
  activeDynamicFilter: string | null;
  clearLocalSearchQuery: boolean;
  tabLevelActiveFilter: TabLevelFilter;
  tabAccordionStates: Record<string, boolean[]>;
  tabCollapseStates: Record<string, boolean>;
}

const initialState: GlobalSearchAndFiltersState = {
  searchQuery: '',
  activeDynamicFilter: null,
  clearLocalSearchQuery: false,
  tabLevelActiveFilter: {},
  tabAccordionStates: {},
  tabCollapseStates: {},
};

const globalSearchAndFiltersSlice = createSlice({
  name: 'globalSearchAndFilters',
  initialState,
  reducers: {
    handleSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    handleFoodTypeFilters: {
      reducer(
        state,
        action: PayloadAction<{
          foodType: string;
          toggleState: boolean;
        }>,
      ) {
        const { foodType, toggleState } = action.payload;
        if (toggleState) {
          state.activeDynamicFilter = foodType;
        } else {
          state.activeDynamicFilter = null;
        }
      },
      prepare(foodType: string, toggleState: boolean) {
        return {
          payload: {
            foodType,
            toggleState,
          },
        };
      },
    },
    handleFoodTypeFiltersReset: (state) => {
      state.activeDynamicFilter = null;
    },
    handleSearchQueryReset: (state) => {
      state.searchQuery = '';
    },
    handleClearLocalSearchQuery: (state, action: PayloadAction<boolean>) => {
      state.clearLocalSearchQuery = action.payload;
    },
    handleTabLevelActiveFilter: (state, action: PayloadAction<TabLevelFilter>) => {
      state.tabLevelActiveFilter = action.payload;
    },
    handleTabTypeInitialAccordionState: (
      state,
      action: PayloadAction<{
        tabType: string;
        accordionStateArr: boolean[];
      }>,
    ) => {
      const { tabType, accordionStateArr } = action.payload;
      state.tabAccordionStates[tabType] = accordionStateArr;
    },
    handleTabToggleAccordionStates: (
      state,
      action: PayloadAction<{
        value: boolean;
        tabType: string;
        index: number;
      }>,
    ) => {
      const { tabType, index, value } = action.payload;

      if (!state.tabAccordionStates[tabType]) {
        state.tabAccordionStates[tabType] = [];
      }
      state.tabAccordionStates[tabType][index] = value;

      const isAnyOpen = state.tabAccordionStates[tabType].some((v) => v);
      state.tabCollapseStates[tabType] = !isAnyOpen;
    },

    // Collapse All Accordion for a specific tab
    handleTabCollapseState: (
      state,
      action: PayloadAction<{
        tabType: string;
        collapseState: boolean;
      }>,
    ) => {
      const { tabType, collapseState } = action.payload;
      state.tabCollapseStates[tabType] = collapseState;
      if (state.tabAccordionStates[tabType]) {
        state.tabAccordionStates[tabType] = state.tabAccordionStates[tabType].map(
          () => !collapseState,
        );
      }
    },

    // Reset Accordion states for a specific tab
    handleResetTabAccordionState: (state, action: PayloadAction<string>) => {
      const tabType = action.payload;
      state.tabCollapseStates[tabType] = false;
      state.tabAccordionStates[tabType] = [];
    },
  },
});

export const {
  handleSearchQuery,
  handleFoodTypeFilters,
  handleFoodTypeFiltersReset,
  handleSearchQueryReset,
  handleClearLocalSearchQuery,
  handleTabLevelActiveFilter,
  handleTabCollapseState,
  handleTabToggleAccordionStates,
  handleTabTypeInitialAccordionState,
  handleResetTabAccordionState,
} = globalSearchAndFiltersSlice.actions;

export default globalSearchAndFiltersSlice.reducer;
